import React from 'react';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { Shield } from 'lucide-react';

const WRAPPER_SX = {
  position: 'sticky',
  top: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const CARD_SX = {
  border: '1px solid #000',
  p: 2,
  bgcolor: '#fff',
};

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#64748B',
  mb: 1.5,
};

const ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1.5,
  py: 0.75,
  borderBottom: '1px solid rgba(0,0,0,0.06)',
};

const ROW_LAST_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1.5,
  py: 0.75,
};

const ROW_LABEL_SX = { fontSize: 12, color: '#64748B' };
const ROW_VALUE_SX = {
  fontSize: 12,
  fontWeight: 700,
  color: '#000',
  textAlign: 'right',
  maxWidth: '60%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const HIPAA_BOX_SX = {
  border: '1px solid #000',
  bgcolor: '#F8F9FC',
  p: '12px 14px',
  display: 'flex',
  gap: 1.5,
  alignItems: 'flex-start',
};

const HIPAA_ICON_SX = { color: '#000', fontSize: 20, mt: '2px', flexShrink: 0 };
const HIPAA_TEXT_SX = { fontSize: 12, color: '#000', lineHeight: 1.5 };

const ACTIONS_WRAP_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const SEND_BTN_SX = {
  bgcolor: '#000',
  color: '#fff',
  '&:hover': { bgcolor: '#1A1A1A' },
  '&.Mui-disabled': { bgcolor: '#A0ABBB', color: '#fff' },
  fontWeight: 700,
  textTransform: 'none',
};

const OUTLINED_BTN_SX = {
  borderColor: '#000',
  color: '#000',
  '&:hover': { borderColor: '#000', bgcolor: '#F8F9FC' },
  textTransform: 'none',
};

const DRAFT_BTN_SX = {
  color: '#000',
  textTransform: 'none',
};

const FOOTER_SX = {
  fontSize: 11,
  color: '#64748B',
  mt: 1,
  textAlign: 'center',
};

const patientLabel = (patient) => {
  if (!patient) return '—';
  const first = patient.first_name || '';
  const last = patient.last_name || '';
  const name = `${first} ${last}`.trim();
  return name || '—';
};

const ComposeSummaryRail = ({
  patient,
  providerEmail,
  documentsCount,
  expiresInDays,
  submitting,
  phiDetected,
  onCancel,
  onSaveDraft,
  onSend,
}) => {
  const sendDisabled = submitting || !documentsCount || phiDetected;

  return (
    <Box sx={WRAPPER_SX}>
      <Box sx={CARD_SX}>
        <Typography sx={SECTION_LABEL_SX}>SUMMARY</Typography>
        <Box sx={ROW_SX}>
          <Typography sx={ROW_LABEL_SX}>Patient</Typography>
          <Typography sx={ROW_VALUE_SX}>{patientLabel(patient)}</Typography>
        </Box>
        <Box sx={ROW_SX}>
          <Typography sx={ROW_LABEL_SX}>Documents</Typography>
          <Typography sx={ROW_VALUE_SX}>{documentsCount || 0}</Typography>
        </Box>
        <Box sx={ROW_LAST_SX}>
          <Typography sx={ROW_LABEL_SX}>Expires</Typography>
          <Typography sx={ROW_VALUE_SX}>
            {expiresInDays ? `${expiresInDays} days` : '—'}
          </Typography>
        </Box>
      </Box>

      <Box sx={HIPAA_BOX_SX}>
        <Shield size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <Typography sx={HIPAA_TEXT_SX}>
          HIPAA-secure delivery. Documents are never sent as attachments. Provider
          authenticates on a one-time link.
        </Typography>
      </Box>

      <Box sx={ACTIONS_WRAP_SX}>
        <Button
          variant="contained"
          disableElevation
          onClick={onSend}
          disabled={sendDisabled}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          sx={SEND_BTN_SX}
        >
          {submitting ? 'Sending…' : 'Save & send'}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={submitting}
            fullWidth
            sx={OUTLINED_BTN_SX}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={onSaveDraft}
            disabled={submitting}
            fullWidth
            sx={DRAFT_BTN_SX}
          >
            Save draft
          </Button>
        </Stack>
        <Typography sx={FOOTER_SX}>
          Provider will be emailed at{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {providerEmail || '—'}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default ComposeSummaryRail;
